import { html, css, nothing } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { registerPasskey, webauthnSupported } from '../auth/webauthn.js';
import { navigate } from '../router.js';
import type { SsoTextField } from '../components/sso-text-field.js';
import '../components/sso-card.js';
import '../components/sso-text-field.js';
import '../components/sso-button.js';
import '../components/sso-alert.js';

/**
 * `<passkey-register-page>` — WebAuthn create() enrollment ceremony (SSO-MOCKUPS.md §5a).
 *
 * Shows a short explanation + optional credential name, then starts the native OS dialog via the
 * `registerPasskey` helper. On success the server returns `{ next }` (typically recovery codes the
 * first time) or a terminal `{ redirect }`. Offers a skip/other-method link to the TOTP path.
 */
@customElement('passkey-register-page')
@localized()
export class PasskeyRegisterPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      .hero {
        display: grid;
        justify-items: center;
        gap: var(--sso-space);
        text-align: center;
      }
      .badge {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: color-mix(in srgb, var(--sso-color-brand) 14%, var(--sso-color-surface));
        display: grid;
        place-items: center;
        font-size: 2rem;
      }
      form {
        display: grid;
        gap: var(--sso-space);
        margin-top: var(--sso-space);
      }
      .skip {
        text-align: center;
        margin-top: var(--sso-space);
      }
    `,
  ];

  @state() private error = '';
  @state() private busy = false;
  @query('sso-text-field') private nameField?: SsoTextField;

  override firstUpdated(): void {
    if (!webauthnSupported()) {
      // No WebAuthn → push the user to the always-available TOTP fallback (D6).
      this.error = msg('Passkeys are not supported on this device. Please use an authenticator app instead.');
    }
    this.focusFirstControl();
  }

  private async register(): Promise<void> {
    if (!webauthnSupported()) {
      navigate('/enroll/totp');
      return;
    }
    const displayName = (this.nameField?.value ?? '').trim() || 'Passkey';
    this.busy = true;
    this.error = '';
    try {
      const r = await registerPasskey(displayName);
      // First successful 2FA enrollment returns one-time recovery codes → show them once.
      if ('recoveryCodes' in r && r.recoveryCodes.length) {
        this.flowContext?.setRecoveryCodes(r.recoveryCodes);
        navigate('/recovery-codes');
        return;
      }
      const returnUri = this.resolveReturnUri();
      if (returnUri) { this.redirect(returnUri); return; }
      navigate('/');
    } catch (err) {
      // Distinguish user-cancel from a real failure for a friendlier message.
      if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
        this.error = msg('Passkey setup was cancelled. You can try again.');
      } else {
        this.error = msg('Could not create a passkey. Please try again or use an authenticator app.');
      }
    } finally {
      this.busy = false;
    }
  }

  override render() {
    return html`
      <sso-card>
        <div class="hero">
          <h1>${msg('Register a passkey')}</h1>
          <div class="badge" aria-hidden="true">🔑</div>
          <p>
            ${msg(
              'Use your fingerprint, face recognition or a security key to sign in without a password from now on.',
            )}
          </p>
        </div>
        ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}
        <form @submit=${(e: SubmitEvent) => { e.preventDefault(); void this.register(); }}>
          <sso-text-field
            label=${msg('Name (optional)')}
            placeholder=${msg('e.g. iPhone of Jan')}
            ?disabled=${this.busy}></sso-text-field>
          <sso-button type="submit" variant="primary" ?busy=${this.busy}>
            ${msg('Register passkey')}
          </sso-button>
        </form>
        <p class="skip">
          <a
            href="/enroll/totp"
            @click=${(e: Event) => { e.preventDefault(); navigate('/enroll/totp'); }}>
            ${msg('Skip / use another method')}
          </a>
        </p>
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'passkey-register-page': PasskeyRegisterPage;
  }
}
